import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CSPMResourcesDrawer from './CSPMResourcesDrawer';
import FilterSelectValue from './FilterSelectValue';
import RequestFixButton from '../../RemediationHub/components/RequestFixButton';
import { useSelector } from 'react-redux';
import RemediationPreviewPanel, { isHttpUrlString } from '../../../components/Common/RemediationPreviewPanel';
import {
  ACCOUNT_DETAIL_FONT_FAMILY,
  ACCOUNT_DETAIL_FONT_SX,
} from '../../../components/AccountDetailPolicy/accountDetailTypography';
import { EditAccountTablePagination } from '../../../components/AccountDetailPolicy/editAccountTableControls';

const DEFAULT_COLUMNS = [
  { key: "Title", label: "Title", minWidth: 220, maxWidth: 600 },
  { key: "Severity", label: "Severity", minWidth: 100 },
  { key: "Status", label: "Status", minWidth: 100 },
  { key: "NumResourcesScanned", label: "Resources", minWidth: 200 },
];

/** Matches Comparative Scan / Configuration content cards. */
const CONFIG_CARD_SX = {
  backgroundColor: "#f3f4f6",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
};

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const FILTER_SELECT_SX = {
  minWidth: 140,
  height: 40,
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    height: 40,
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": { color: "#94a3b8" },
};

const SEARCH_FIELD_SX = {
  width: { xs: "100%", sm: 220 },
  minWidth: { xs: 0, sm: 220 },
  ml: { sm: "auto" },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const MENU_ITEM_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#111827",
  mx: 0.5,
  my: 0.25,
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#fff4eb",
  },
  "&.Mui-selected": {
    fontWeight: 600,
    color: "#fff",
    backgroundColor: "#F98125",
    "&:hover": {
      backgroundColor: "#e8741c",
      color: "#fff",
    },
  },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
      border: "1px solid #e2e8f0",
      mt: 0.5,
      maxHeight: 300,
      "& .MuiList-root": {
        py: 0.5,
      },
    },
  },
};

function getSeverityBadgeProps(sev) {
  const s = (sev || '').toUpperCase();
  let color = '#e0e0e0', text = '#333';
  if (s === 'CRITICAL') { color = '#ffebee'; text = '#b71c1c'; }
  else if (s === 'HIGH') { color = '#ffebee'; text = '#f44336'; }
  else if (s === 'MEDIUM') { color = '#fff9c4'; text = '#fbc02d'; }
  else if (s === 'LOW') { color = '#e8f5e9'; text = '#4caf50'; }
  else if (s === 'INFORMATIONAL') { color = '#e3f2fd'; text = '#1976d2'; }
  return { color, text };
}

function getStatusBadgeProps(status) {
  const s = (status || '').toUpperCase();
  let color = '#e0e0e0', text = '#333';
  if (s === 'FAILED') { color = '#ffebee'; text = '#d32f2f'; }
  else if (s === 'PASSED') { color = '#e8f5e9'; text = '#388e3c'; }
  else if (s === 'MANUAL') { color = '#e3f2fd'; text = '#1976d2'; }
  else if (s === 'SKIPPED') { color = '#f3fafd'; text = '#0288d1'; }
  return { color, text };
}

/** Title-case labels for filter options (values stay as returned for data matching). */
function toTitleCaseLabel(value) {
  const raw = String(value || '');
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

const CSPMGrid = ({ scanObj, accountNumber }) => {
  const remediableIds = useSelector((s) => s.remediation?.remediableChecks?.ids) || [];
  const remediableSet = useMemo(() => new Set(remediableIds), [remediableIds]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [remediationPreviewUrl, setRemediationPreviewUrl] = useState(null);

  const openRemediationPreview = useCallback((url) => {
    if (isHttpUrlString(url)) setRemediationPreviewUrl(String(url).trim());
    else if (url) window.open(String(url), "_blank", "noopener,noreferrer");
  }, []);

  const closeRemediationPreview = useCallback(() => {
    setRemediationPreviewUrl(null);
  }, []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [drawerAvailableRegions, setDrawerAvailableRegions] = useState([]);
  const [drawerAvailableTypes, setDrawerAvailableTypes] = useState([]);
  const [drawerCheckTitle, setDrawerCheckTitle] = useState("");
  const availableStatuses = useMemo(() => {
    // Collect unique statuses from checks
    const arr = scanObj?.auditChecks?.Checks || [];
    return Array.from(new Set(arr.map(c => c.Status))).filter(Boolean);
  }, [scanObj]);
  const availableSeverities = useMemo(() => {
    const arr = scanObj?.auditChecks?.Checks || [];
    return Array.from(new Set(arr.map(c => (c.Severity || '').toUpperCase()))).filter(Boolean);
  }, [scanObj]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const openResourcesDrawer = (row, filter = null) => {
    if (!row?.CheckId) return;
    setSelectedCheckId(row.CheckId);
    setSelectedRunId(scanObj?.runId);
    setDrawerAvailableRegions(row.AvailableRegions || []);
    setDrawerAvailableTypes(row.AvailableTypes || []);
    setDrawerCheckTitle(row.Title || "");
    setDrawerOpen(true);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("setCspmFilter", { detail: { filter } })
      );
    }, 100);
  };

  // Table data
  const severityOrder = {
    'CRITICAL': 1,
    'HIGH': 2,
    'MEDIUM': 3,
    'LOW': 4,
    'INFORMATIONAL': 5
  };
  const checks = useMemo(() => {
    const arr = scanObj?.auditChecks?.Checks ? [...scanObj.auditChecks.Checks] : [];
    return arr.sort((a, b) => {
      // On initial load only (no user column sort), surface remediable checks first.
      if (!sortConfig.key) {
        const aRem = remediableSet.has(a.CheckId) ? 0 : 1;
        const bRem = remediableSet.has(b.CheckId) ? 0 : 1;
        if (aRem !== bRem) return aRem - bRem;
      }
      // Then by Status: FAILED first
      if ((a.Status === 'FAILED') && (b.Status !== 'FAILED')) return -1;
      if ((a.Status !== 'FAILED') && (b.Status === 'FAILED')) return 1;
      // Then by Severity order
      const sevA = severityOrder[(a.Severity || '').toUpperCase()] || 99;
      const sevB = severityOrder[(b.Severity || '').toUpperCase()] || 99;
      return sevA - sevB;
    });
  }, [scanObj, remediableSet, sortConfig.key]);

  // Sorting logic
  const sortedChecks = useMemo(() => {
    // User column sort takes over; otherwise keep remediable-first order from `checks`.
    let arr = [...checks];
    if (sortConfig.key) {
      arr.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === 'NumResourcesScanned') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          aVal = (aVal || '').toString().toUpperCase();
          bVal = (bVal || '').toString().toUpperCase();
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [checks, sortConfig]);

  // Compute unique list of all AvailableTypes (services)
  const availableServices = useMemo(() => {
    if (!scanObj?.auditChecks?.Checks) return [];
    const allTypes = scanObj.auditChecks.Checks.flatMap(check => check.AvailableTypes || []);
    return Array.from(new Set(allTypes)).sort();
  }, [scanObj]);

  // Filtering logic
  useEffect(() => {
    let rows = sortedChecks;
    if (selectedStatus !== 'all') {
      rows = rows.filter(check => check.Status === selectedStatus);
    }
    if (selectedSeverity !== 'all') {
      rows = rows.filter(check => (check.Severity || '').toUpperCase() === selectedSeverity);
    }
    if (selectedService !== 'all') {
      rows = rows.filter(check => (check.AvailableTypes || []).includes(selectedService));
    }
    if (activeFilters.length > 0) {
      rows = rows.filter(check => activeFilters.includes(check.Status?.toLowerCase()));
    }
    // Search across all visible data fields: Title, Severity, Status, Description, Remediation
    if (search) {
      const searchLower = search.toLowerCase();
      rows = rows.filter(check => {
        const title = check.Title?.toLowerCase() || '';
        const severity = (check.Severity || '').toLowerCase();
        const status = (check.Status || '').toLowerCase();
        const description = check.Description?.toLowerCase() || '';
        let remediation = '';
        if (typeof check.Remediation === 'string') {
          remediation = check.Remediation.toLowerCase();
        } else if (typeof check.Remediation === 'object' && check.Remediation?.Text) {
          remediation = check.Remediation.Text.toLowerCase();
        }
        return (
          title.includes(searchLower) ||
          severity.includes(searchLower) ||
          status.includes(searchLower) ||
          description.includes(searchLower) ||
          remediation.includes(searchLower)
        );
      });
    }
    setFilteredRows(rows);
  }, [sortedChecks, selectedStatus, selectedSeverity, selectedService, activeFilters, search]);

  // Get visible columns
  const visibleColumns = DEFAULT_COLUMNS;
  // Get paginated rows
  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Empty/error state
  if (scanObj === undefined || scanObj === null) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress color="primary" />
        <Typography variant="h6" color="text.secondary" mt={2}>
          Loading scan data...
        </Typography>
      </Box>
    );
  }
  if (scanObj && (!checks || !checks.length)) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No checks found for this scan.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: { xs: 1.5, sm: 2.5 },
        ...CONFIG_CARD_SX,
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}>
          Checks: {filteredRows.length}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Select
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={FILTER_SELECT_SX}
          MenuProps={selectMenuProps}
          inputProps={{ "aria-label": "Filter by status" }}
          renderValue={(val) => (
            <FilterSelectValue
              label="Status"
              value={val === "all" ? "All" : toTitleCaseLabel(val)}
            />
          )}
        >
          <MenuItem value="all" sx={MENU_ITEM_SX}>All</MenuItem>
          {availableStatuses.map((status) => (
            <MenuItem key={status} value={status} sx={MENU_ITEM_SX}>
              {toTitleCaseLabel(status)}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={selectedSeverity}
          onChange={(e) => {
            setSelectedSeverity(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={FILTER_SELECT_SX}
          MenuProps={selectMenuProps}
          inputProps={{ "aria-label": "Filter by severity" }}
          renderValue={(val) => (
            <FilterSelectValue
              label="Severity"
              value={val === "all" ? "All" : toTitleCaseLabel(val)}
            />
          )}
        >
          <MenuItem value="all" sx={MENU_ITEM_SX}>All</MenuItem>
          {availableSeverities.map((sev) => (
            <MenuItem key={sev} value={sev} sx={MENU_ITEM_SX}>
              {toTitleCaseLabel(sev)}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={selectedService}
          onChange={(e) => {
            setSelectedService(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={FILTER_SELECT_SX}
          MenuProps={selectMenuProps}
          inputProps={{ "aria-label": "Filter by service" }}
          renderValue={(val) => (
            <FilterSelectValue label="Service" value={val === "all" ? "All" : val} />
          )}
        >
          <MenuItem value="all" sx={MENU_ITEM_SX}>All</MenuItem>
          {availableServices.map((service) => (
            <MenuItem key={service} value={service} sx={MENU_ITEM_SX}>
              {service}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={SEARCH_FIELD_SX}
        />
      </Box>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col) => {
                  const isSortable = col.key === "NumResourcesScanned";
                  return (
                    <TableCell
                      key={col.key}
                      align={col.key === "Title" || col.key === "CheckId" ? "left" : "center"}
                      sx={{
                        ...HEADER_CELL_SX,
                        cursor: isSortable ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={isSortable ? () => handleSort(col.key) : undefined}
                    >
                      {col.label}
                      {col.key === "NumResourcesScanned" && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            ml: 0.5,
                            verticalAlign: "middle",
                            visibility: sortConfig.key === col.key ? "visible" : "hidden",
                            ".MuiTableCell-root:hover &": {
                              visibility: "visible",
                            },
                          }}
                        >
                          {sortConfig.key === col.key ? (
                            sortConfig.direction === "asc" ? (
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            )
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                          )}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align="center"
                    sx={{ ...BODY_CELL_SX, py: 5 }}
                  >
                    <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                      No checks found for this scan.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row, idx) => (
                  <React.Fragment key={row.CheckId || idx}>
                    <TableRow
                      hover
                      selected={expandedRow === (row.CheckId || idx)}
                      sx={{
                        "&:hover": { backgroundColor: "#f8fafc" },
                        "&.Mui-selected": { backgroundColor: "#fff7ed" },
                        "&.Mui-selected:hover": { backgroundColor: "#ffedd5" },
                      }}
                    >
                      {visibleColumns.map((col) => {
                        if (col.key === "Title") {
                          return (
                            <TableCell
                              key={col.key}
                              align="left"
                              sx={{ ...BODY_CELL_SX, minWidth: 220, maxWidth: 600 }}
                            >
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setExpandedRow(
                                      expandedRow === (row.Title || idx) ? null : row.Title || idx
                                    )
                                  }
                                  aria-label={
                                    expandedRow === (row.Title || idx)
                                      ? "Collapse row"
                                      : "Expand row"
                                  }
                                  sx={{
                                    mt: "-2px",
                                    color: "#94a3b8",
                                    "&:hover": {
                                      color: "#64748b",
                                      backgroundColor: "#f1f5f9",
                                    },
                                  }}
                                >
                                  {expandedRow === (row.Title || idx) ? (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  ) : (
                                    <KeyboardArrowRightIcon fontSize="small" />
                                  )}
                                </IconButton>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      ...CELL_TEXT,
                                      color: "#111827",
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                    }}
                                    title={row.Title}
                                  >
                                    {row.Title}
                                  </Typography>
                                  <Box sx={{ mt: 0.5 }}>
                                    <RequestFixButton
                                      checkId={row.CheckId}
                                      failedCount={row.NumResourcesFailed}
                                      onClick={() => openResourcesDrawer(row, "FAILED")}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </TableCell>
                          );
                        }
                        if (col.key === "Severity") {
                          const sev = (row.Severity || "").toUpperCase();
                          const { text } = getSeverityBadgeProps(sev);
                          return (
                            <TableCell key={col.key} align="center" sx={BODY_CELL_SX}>
                              <Typography sx={{ color: text, fontWeight: 600, fontSize: "0.8125rem" }}>
                                {sev || "—"}
                              </Typography>
                            </TableCell>
                          );
                        }
                        if (col.key === "Status") {
                          const { text } = getStatusBadgeProps(row.Status);
                          return (
                            <TableCell key={col.key} align="center" sx={BODY_CELL_SX}>
                              <Typography sx={{ color: text, fontWeight: 600, fontSize: "0.8125rem" }}>
                                {row.Status || "—"}
                              </Typography>
                            </TableCell>
                          );
                        }
                        if (col.key === "Type") {
                          return (
                            <TableCell key={col.key} align="center" sx={BODY_CELL_SX}>
                              {row.Type || row.RiskType}
                            </TableCell>
                          );
                        }
                        if (col.key === "Region") {
                          return (
                            <TableCell key={col.key} align="center" sx={BODY_CELL_SX}>
                              {row.Region}
                            </TableCell>
                          );
                        }
                        if (col.key === "NumResourcesScanned") {
                          const totalResources = row.NumResourcesScanned || 0;
                          const failedResources = row.NumResourcesFailed || 0;
                          const passedResources = totalResources - failedResources;
                          const isZero = totalResources === 0;

                          return (
                            <TableCell key={col.key} align="center" sx={BODY_CELL_SX}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  justifyContent: "center",
                                  width: "100%",
                                  minHeight: 32,
                                  alignItems: "center",
                                }}
                              >
                                <Box sx={{ width: "33%", display: "flex", justifyContent: "center" }}>
                                  <Tooltip
                                    title={`${failedResources} failed ${
                                      totalResources === 1 ? "resource" : "resources"
                                    }`}
                                  >
                                    <span>
                                      <Chip
                                        label={`Failed: ${failedResources}`}
                                        size="small"
                                        onClick={
                                          isZero
                                            ? undefined
                                            : () => openResourcesDrawer(row, "FAILED")
                                        }
                                        sx={{
                                          backgroundColor: isZero ? "#f1f5f9" : "#fef2f2",
                                          color: isZero ? "#94a3b8" : "#dc2626",
                                          cursor: isZero ? "not-allowed" : "pointer",
                                          opacity: isZero ? 0.5 : 1,
                                          pointerEvents: isZero ? "none" : "auto",
                                          "&:hover": isZero ? {} : { backgroundColor: "#fee2e2" },
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          height: 24,
                                          borderRadius: "6px",
                                          minWidth: 100,
                                          maxWidth: 120,
                                        }}
                                        disabled={isZero}
                                      />
                                    </span>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ width: "33%", display: "flex", justifyContent: "center" }}>
                                  <Tooltip
                                    title={`${passedResources} passed ${
                                      totalResources === 1 ? "resource" : "resources"
                                    }`}
                                  >
                                    <span>
                                      <Chip
                                        label={`Passed: ${passedResources}`}
                                        size="small"
                                        onClick={
                                          isZero
                                            ? undefined
                                            : () => openResourcesDrawer(row, "PASSED")
                                        }
                                        sx={{
                                          backgroundColor: isZero ? "#f1f5f9" : "#ecfdf5",
                                          color: isZero ? "#94a3b8" : "#16a34a",
                                          cursor: isZero ? "not-allowed" : "pointer",
                                          opacity: isZero ? 0.5 : 1,
                                          pointerEvents: isZero ? "none" : "auto",
                                          "&:hover": isZero ? {} : { backgroundColor: "#d1fae5" },
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          height: 24,
                                          borderRadius: "6px",
                                          minWidth: 100,
                                          maxWidth: 120,
                                        }}
                                        disabled={isZero}
                                      />
                                    </span>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ width: "33%", display: "flex", justifyContent: "center" }}>
                                  <Tooltip
                                    title={`${totalResources} total ${
                                      totalResources === 1 ? "resource" : "resources"
                                    }`}
                                  >
                                    <span>
                                      <Chip
                                        label={`Total: ${totalResources}`}
                                        size="small"
                                        onClick={
                                          isZero
                                            ? undefined
                                            : () => openResourcesDrawer(row, null)
                                        }
                                        sx={{
                                          backgroundColor: isZero ? "#f1f5f9" : "#eff6ff",
                                          color: isZero ? "#94a3b8" : "#2563eb",
                                          cursor: isZero ? "not-allowed" : "pointer",
                                          opacity: isZero ? 0.5 : 1,
                                          pointerEvents: isZero ? "none" : "auto",
                                          "&:hover": isZero ? {} : { backgroundColor: "#dbeafe" },
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          height: 24,
                                          borderRadius: "6px",
                                          minWidth: 100,
                                          maxWidth: 120,
                                        }}
                                        disabled={isZero}
                                      />
                                    </span>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                    {expandedRow === (row.Title || idx) && (
                      <TableRow>
                        <TableCell
                          colSpan={visibleColumns.length}
                          sx={{
                            backgroundColor: "#f8fafc",
                            borderBottom: "1px solid #f1f5f9",
                            px: 1.75,
                            py: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ mb: 0.5, color: "#111827", fontSize: "0.8125rem", fontWeight: 700 }}
                            >
                              Description:
                              <Typography
                                component="span"
                                sx={{ ml: 1, color: "#334155", fontWeight: 500 }}
                              >
                                {row.Description}
                              </Typography>
                            </Typography>
                            <Typography
                              sx={{ mb: 0.5, color: "#111827", fontSize: "0.8125rem", fontWeight: 700 }}
                            >
                              Recommendation:
                              <Typography
                                component="span"
                                sx={{ ml: 1, color: "#334155", fontWeight: 500 }}
                              >
                                {typeof row.Remediation === "object"
                                  ? row.Remediation.Text
                                  : row.Remediation}
                              </Typography>
                              {row.Remediation?.Url && (
                                <Typography
                                  component="button"
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openRemediationPreview(row.Remediation.Url);
                                  }}
                                  sx={{
                                    display: "inline",
                                    ml: 1,
                                    p: 0,
                                    border: "none",
                                    background: "none",
                                    color: "#F98125",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    font: "inherit",
                                  }}
                                >
                                  Reference
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <EditAccountTablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={filteredRows.length}
          rowsPerPageOptions={[10, 20, 30, 50]}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(0);
          }}
        />
      </Box>
      <CSPMResourcesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        checkId={selectedCheckId}
        runId={selectedRunId}
        availableRegions={drawerAvailableRegions}
        availableTypes={drawerAvailableTypes}
        checkTitle={drawerCheckTitle}
        accountNumber={accountNumber}
        isRemediable={Boolean(selectedCheckId && remediableSet.has(selectedCheckId))}
      />
      <RemediationPreviewPanel
        url={remediationPreviewUrl}
        onClose={closeRemediationPreview}
      />
    </Paper>
  );
};

export default CSPMGrid; 